import type { ECard } from '@graphql/generated-base';
import { useChatGPTTextCompletionQuery } from '../../../apps/admin/postOffice/PostOffice.graphql.generated';
import { AiModelVersion } from '@graphql/generated-base';
import { useState } from 'react';
import type { GiftWrapType } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useEventLocale } from '@localization/context/LocaleContext';
import { sanitizeLocales, tryParseLocaleRegion } from '@localization/safeIntl';
import { DEFAULT_LANGUAGE_BASE } from '@shared/core/i18n/i18n.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addError } from '@shared/utils/logger';

interface GiftNoteParams {
  prompt: string;
  fallback: string;
  skip?: boolean;
  onCompleted: (note: string, prompt: string) => void;
}

const FORBIDDEN_PHRASES = new Set([
  'husband',
  'wife',
  'bride',
  'groom',
  'mr',
  'mrs',
  'sir',
  'madam',
  'lady',
  'father',
  'mother',
  'dad',
  'mom',
  'man',
  'maid',
  'girl',
  'woman',
  'boy',
]);

const FORBIDDEN_PHRASE_EXAMPLES = Array.from(FORBIDDEN_PHRASES).join(', ');

const isEnglishLanguage = (language?: string | null) => {
  if (!language) return true;
  return language.toLowerCase().split('-')[0] === DEFAULT_LANGUAGE_BASE;
};

export const containsForbiddenPhrase = (text: string, language?: string | null) => {
  if (!isEnglishLanguage(language)) return false;

  const words = text.toLowerCase().match(/\b\w+\b/g) || [];
  return words.some(word => FORBIDDEN_PHRASES.has(word));
};

export const useGenerateGiftNote = ({ prompt, fallback, skip, onCompleted }: GiftNoteParams) => {
  const { language } = useEventLocale();
  const { loading: isEventInfoLoading } = useEventInfo();
  const [originalNote, setOriginalNote] = useState('');
  const { loading: isCompletionLoading } = useChatGPTTextCompletionQuery({
    variables: {
      prompt,
      modelVersion: AiModelVersion.gpt4oMini,
    },
    skip: skip || isEventInfoLoading || !!originalNote,
    batchMode: 'off',
    onCompleted(data) {
      // ChatGPT often returns noise characters (quotes, dashes, whitespace) at the beginning.
      // Trim any leading characters that aren't a letter or number in any script, then trim trailing spaces.
      const note = data.textCompletion.replace(/^[^\p{L}\p{N}]+/u, '').trimEnd();
      if (containsForbiddenPhrase(note, language)) {
        addError('Generated note contains forbidden phrases, using fallback instead.', { action: 'giftNoteForbiddenPhrase', note, prompt, language });
        setOriginalNote(fallback);
        onCompleted(fallback, prompt);
        return;
      }

      setOriginalNote(note);
      onCompleted(note, prompt);
    },
    onError(error) {
      addError(error, { action: 'giftNoteGenerationError', prompt, language });
      setOriginalNote(fallback);
      onCompleted(fallback, prompt);
    },
  });

  return { originalNote, isGeneratedMessageLoading: isCompletionLoading || (!skip && isEventInfoLoading) };
};

export const prepareECardPayload = (payload: { price: number; draftId: string; theme: string; imageURL?: string; offerType: GiftWrapType }): ECard => {
  // eCard processing requires price in minor units (cents with no decimals)
  const priceInMajorUnits = payload.price * 100;
  return {
    price: priceInMajorUnits,
    emailDraftId: payload.draftId,
    quantity: 1,
    type: 'GIFT_WRAP_BASIC',
    details: [{ key: 'theme', value: payload.theme }],
    imageURL: payload.imageURL,
    offerType: payload.offerType,
  };
};

// Workaround: price estimates need an eCard payload to calculate taxes before a
// draft exists, while the sample draft ID and theme are ignored by the calculation.
export const prepareSampleECardPayload = (price: number, offerType: GiftWrapType): ECard =>
  prepareECardPayload({
    price,
    draftId: 'sample-ecard',
    theme: 'sample-ecard',
    offerType,
  });

export const parseGiftWrapPayload = (payload: string) => {
  try {
    return JSON.parse(payload) as { message: string; themeId: string; image: string; store?: string; title: string; giftLink: string };
  } catch {
    return null;
  }
};

const countryNameByRegion = new Map<string, string | undefined>();

const countryNameFromRegion = (region: string): string | undefined => {
  if (countryNameByRegion.has(region)) return countryNameByRegion.get(region);

  let countryName: string | undefined;
  try {
    // eslint-disable-next-line compat/compat -- missing on old Safari; the catch drops the country and the prompt omits its culture line
    countryName = new Intl.DisplayNames(sanitizeLocales(['en']), { type: 'region', fallback: 'none' }).of(region) ?? undefined;
  } catch {
    countryName = undefined;
  }
  countryNameByRegion.set(region, countryName);
  return countryName;
};

export const countryNameFromLocale = (locale?: string | null): string | undefined => {
  const region = tryParseLocaleRegion(locale);
  return region ? countryNameFromRegion(region) : undefined;
};

/**
 * `locationInfo.country` is usually a display name ("United Kingdom"), but some events store an
 * ISO 3166-1 alpha-2 code ("GB"). Expand the code so the register lookup matches and the prompt's
 * culture line names a country the model recognises rather than two letters. An unrecognised code
 * passes through untouched.
 */
export const expandCountryCode = (country: string): string => {
  if (!/^[A-Za-z]{2}$/.test(country)) return country;
  return countryNameFromRegion(country.toUpperCase()) ?? country;
};

export const resolveGiftWrapCountry = ({ venueCountry, locale }: { venueCountry?: string | null; locale?: string | null }): string | undefined => {
  const fromVenue = venueCountry?.trim();
  if (fromVenue) return expandCountryCode(fromVenue);
  return countryNameFromLocale(locale);
};

export const useGiftNoteLocale = (): { language: string; country?: string } => {
  const { language } = useEventLocale();
  const { eventInfo } = useEventInfo();
  return { language, country: resolveGiftWrapCountry({ venueCountry: eventInfo?.locationInfo?.country, locale: eventInfo?.locale }) };
};

/**
 * Per-country register + one example, appended to the gift wrap prompt only for the matched country.
 * Derived from ~290 real UK / ~270 US guest rewrites (AIDraftEdited, Jun-Sep 2026) plus country etiquette
 * guides (Hitched, Bridebook, Papier; Zola). No guest text is reproduced verbatim.
 * Australia / New Zealand map to the UK register: a blind judge could not separate AU from UK/US in a
 * 3-sentence note (0/12 across three example sources), so an AU-specific block measurably does nothing.
 * The copy is wedding-specific, so it is not appended for baby registries.
 * Eval: artifacts.joyboo.ai/gift-wrap-example-source-2026-09-17
 */
export type GiftWrapPromptContext = 'wedding' | 'baby';

export type GiftWrapCountryRegister = Readonly<{ register: string; example: string }>;

const UK_REGISTER: GiftWrapCountryRegister = {
  register:
    'Write it the way a guest from this country actually writes a card: open with "Dear <names>," then "Congratulations on your special day!" or "Huge congratulations" or "Many congratulations". Warm, personal, slightly understated; "so happy for you both", "hope you have a wonderful/lovely time", "wishing you every happiness as you begin married life", "wishing you a lifetime of love and happiness". Structure: congratulations, one personal line, hopes for their future. Keep it brief; formal is fine, effusive is not. British/Commonwealth English spelling. Guests here almost never say "cheers to", "you guys", "awesome", or "so excited for you".',
  example:
    'Dear Amy and Bob, many congratulations on your wedding day. We are so happy for you both and hope the day is everything you wished for. Hope the new knives get plenty of use as you settle into married life. Wishing you every happiness together.',
};

const US_REGISTER: GiftWrapCountryRegister = {
  register:
    'Write it the way a guest from this country actually writes a card: open with "Dear <names>," or just the names, then "Congratulations on your special day!" Upbeat and direct: "so excited for you two", "can\'t wait to celebrate", "cheers to the happy couple", "here\'s to a long and happy marriage", "you guys" is fine. Structure: congratulations, warm wishes, one personal line. American spelling; exclamation points are normal.',
  example:
    "Dear Molly and Adam, congratulations on your wedding! So happy for you two as you start this new chapter together. Hope these knives help you make a ton of great meals. Here's to a long and happy marriage!",
};

export const GIFT_WRAP_COUNTRY_REGISTERS: Readonly<Record<string, GiftWrapCountryRegister>> = {
  'United Kingdom': UK_REGISTER,
  Australia: UK_REGISTER,
  'New Zealand': UK_REGISTER,
  Ireland: UK_REGISTER,
  'United States': US_REGISTER,
};

const COUNTRY_REGISTERS_BY_NORMALIZED_NAME = new Map<string, GiftWrapCountryRegister>(
  Object.entries(GIFT_WRAP_COUNTRY_REGISTERS).map(([country, register]) => [country.trim().toLowerCase(), register]),
);

export const getGiftWrapCountryRegister = (country?: string | null): GiftWrapCountryRegister | undefined => {
  if (!country) return undefined;
  return COUNTRY_REGISTERS_BY_NORMALIZED_NAME.get(country.trim().toLowerCase());
};

const buildRegisterLines = (country: string | undefined, context: string) => {
  const register = context === 'wedding' ? getGiftWrapCountryRegister(country) : undefined;
  if (!register) return '';
  return `\nRegister for ${country}: ${register.register}\nExample of the register (do not copy; write a new note for the gift above): ${register.example}`;
};

const buildCultureLine = (country: string | undefined, noteDescription: string) =>
  country
    ? `Culture: write the way a guest in ${country} would write a ${noteDescription}. Match local greeting customs and register, not US defaults. Same language family is not the same culture (UK ≠ Australia ≠ US).`
    : '';

const INCLUSIVE_LANGUAGE_LINES = `Language: inclusive—avoid gendered roles or titles (e.g., ${FORBIDDEN_PHRASE_EXAMPLES}).
Pronouns: address the recipients as "you," "you both," or "you two"; avoid gendered pronouns and honorifics; avoid third-person references—if necessary, use they/them/their; do not assume a shared last name or future roles.`;

export const generateGiftWrapPrompt = ({
  partners,
  context = 'wedding',
  from,
  giftName,
  language = 'en-US',
  country,
}: {
  partners: string;
  context?: GiftWrapPromptContext;
  from: string;
  giftName: string;
  language?: string;
  country?: string;
}) =>
  [
    `Write a short, first-person ${context} gift note in the language of the locale "${language}" from ${from} to ${partners}.`,
    'Tone: warm and friendly.',
    buildCultureLine(country, `${context} gift note`),
    `Content: reference their ${giftName} (no mention of any relationship between the giver and recipients).`,
    INCLUSIVE_LANGUAGE_LINES,
    `Names: use names exactly as provided and in this order: ${partners}.`,
    'Length: 2–4 sentences.',
    "Prohibitions: no religious references, no emojis, no hashtags, no quotes, no sign-off or signature line (the sender's name is printed separately on the card).",
    'Output: the note text only, with no headings or explanations.',
    buildRegisterLines(country, context),
  ]
    .filter(Boolean)
    .join('\n');
